import React, { useState, useMemo, useEffect } from 'react';
import { syncedGet, syncedSet, serverGet, serverSet } from '../data/serverStore';

const HOURS_KEY   = 'sl_hours_log';
const PAY_KEY     = 'sl_pay_notices';

// ── Data helpers ──────────────────────────────────────────────────────────────
function loadEntries()     { return syncedGet(HOURS_KEY, []); }
function saveEntries(v)    { syncedSet(HOURS_KEY, v); }
function loadPayNotices()  { return syncedGet(PAY_KEY, []); }
function savePayNotices(v) { syncedSet(PAY_KEY, v); }

function today()    { return new Date().toISOString().split('T')[0]; }
function weekStart(){ const d=new Date(); d.setDate(d.getDate()-d.getDay()); return d.toISOString().split('T')[0]; }
function toMins(t)  { if(!t)return 0; const[h,m]=t.split(':').map(Number); return h*60+(m||0); }
function fmtH(mins) { if(!mins)return'—'; const h=Math.floor(mins/60),m=mins%60; return m?`${h}h ${m}m`:`${h}h`; }
function fmtDate(d) { if(!d)return'—'; return new Date(d+'T12:00:00').toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',year:'numeric'}); }

// ── Refresh from server ───────────────────────────────────────────────────────
async function pullFresh(key, setter) {
  const val = await serverGet(key);
  if (Array.isArray(val)) {
    syncedSet(key, val);
    setter(val);
  }
}

export default function HoursLog({ session, toast }) {
  const isAdmin   = session?.role === 'admin';
  const username  = session?.username || '';
  const dispName  = session?.displayName || username;

  const [entries,  setEntries]  = useState(loadEntries);
  const [notices,  setNotices]  = useState(loadPayNotices);
  const [tab,      setTab]      = useState('entry');
  const [form,     setForm]     = useState({date:today(),timeIn:'',timeOut:'',breakMins:'30',notes:''});
  const [editId,   setEditId]   = useState(null);
  const [fUser,    setFUser]    = useState('');
  const [fFrom,    setFFrom]    = useState('');
  const [fTo,      setFTo]      = useState('');
  const [delId,    setDelId]    = useState(null);
  const [payModal, setPayModal] = useState(null); // {username, displayName}
  const [payForm,  setPayForm]  = useState({amount:'',payDate:'',note:''});
  const [tick,     setTick]     = useState(0);

  // Pull fresh data from server on mount + every 10s
  useEffect(()=>{
    pullFresh(HOURS_KEY, setEntries);
    pullFresh(PAY_KEY,   setNotices);
    const t = setInterval(()=>{
      pullFresh(HOURS_KEY, setEntries);
      pullFresh(PAY_KEY,   setNotices);
    }, 10000);
    return ()=>clearInterval(t);
  },[]);

  const set = (k,v)=>setForm(p=>({...p,[k]:v}));
  const calcMins=(i,o,b)=>{ const d=toMins(o)-toMins(i)-(parseInt(b)||0); return d>0?d:0; };
  const preview = calcMins(form.timeIn,form.timeOut,form.breakMins);

  // ── Save entry ──────────────────────────────────────────────────────────────
  const handleSave = () => {
    if(!form.date)   {toast('error','Date required');return;}
    if(!form.timeIn) {toast('error','Time In required');return;}
    if(!form.timeOut){toast('error','Time Out required');return;}
    if(toMins(form.timeOut)<=toMins(form.timeIn)){toast('error','Time Out must be after Time In');return;}
    const mins=calcMins(form.timeIn,form.timeOut,form.breakMins);
    const all=loadEntries();
    if(editId){
      const i=all.findIndex(e=>e.id===editId);
      if(i!==-1) all[i]={...all[i],...form,mins,updatedAt:new Date().toISOString()};
      setEditId(null); toast('success','Entry updated');
    } else {
      all.unshift({id:Date.now().toString(36)+Math.random().toString(36).slice(2,5),
        username,displayName:dispName,...form,
        breakMins:parseInt(form.breakMins)||0,mins,
        submittedAt:new Date().toISOString(),updatedAt:null});
      toast('success',`✅ Logged ${fmtH(mins)} for ${fmtDate(form.date)}`);
    }
    saveEntries(all); setEntries(all);
    setForm({date:today(),timeIn:'',timeOut:'',breakMins:'30',notes:''});
  };

  const handleEdit=(e)=>{
    setForm({date:e.date,timeIn:e.timeIn,timeOut:e.timeOut,
      breakMins:String(e.breakMins||30),notes:e.notes||''});
    setEditId(e.id); setTab('entry');
  };

  const handleDelete=(id)=>{
    const all=loadEntries().filter(e=>e.id!==id);
    saveEntries(all); setEntries(all); setDelId(null);
    toast('info','Entry deleted');
  };

  // ── My data ─────────────────────────────────────────────────────────────────
  const mine = useMemo(()=>
    entries.filter(e=>e.username===username).sort((a,b)=>b.date.localeCompare(a.date)),
  [entries,username]);
  const myTotal    = mine.reduce((a,e)=>a+(e.mins||0),0);
  const myWeek     = mine.filter(e=>e.date>=weekStart()).reduce((a,e)=>a+(e.mins||0),0);
  const myMonth    = mine.filter(e=>e.date.startsWith(today().slice(0,7))).reduce((a,e)=>a+(e.mins||0),0);

  // My unread pay notices
  const myNotices = useMemo(()=>
    notices.filter(n=>n.username===username).sort((a,b)=>b.sentAt.localeCompare(a.sentAt)),
  [notices,username]);
  const unread = myNotices.filter(n=>!n.read).length;

  // ── Admin data ───────────────────────────────────────────────────────────────
  const filtered = useMemo(()=>{
    let r=[...entries].sort((a,b)=>b.date.localeCompare(a.date));
    if(fUser) r=r.filter(e=>(e.displayName||'').toLowerCase().includes(fUser.toLowerCase())||(e.username||'').toLowerCase().includes(fUser.toLowerCase()));
    if(fFrom) r=r.filter(e=>e.date>=fFrom);
    if(fTo)   r=r.filter(e=>e.date<=fTo);
    return r;
  },[entries,fUser,fFrom,fTo]);

  const byEmployee = useMemo(()=>{
    const map={};
    entries.forEach(e=>{
      if(!map[e.username]) map[e.username]={username:e.username,displayName:e.displayName,
        mins:0,days:0,lastDate:'',entries:[]};
      map[e.username].mins+=e.mins||0;
      map[e.username].days++;
      map[e.username].entries.push(e);
      if(e.date>map[e.username].lastDate) map[e.username].lastDate=e.date;
    });
    return Object.values(map).sort((a,b)=>b.mins-a.mins);
  },[entries]);

  // ── Send payment notice ──────────────────────────────────────────────────────
  const sendPayNotice = () => {
    if(!payForm.amount){toast('error','Enter payment amount');return;}
    if(!payForm.payDate){toast('error','Enter payment date');return;}
    const all = loadPayNotices();
    all.unshift({
      id: Date.now().toString(36),
      username: payModal.username,
      displayName: payModal.displayName,
      amount: payForm.amount,
      payDate: payForm.payDate,
      note: payForm.note,
      sentAt: new Date().toISOString(),
      sentBy: session?.displayName||'Admin',
      read: false,
    });
    savePayNotices(all); setNotices(all);
    setPayModal(null); setPayForm({amount:'',payDate:'',note:''});
    toast('success',`Payment notice sent to ${payModal.displayName}`);
  };

  const markRead=(id)=>{
    const all=loadPayNotices().map(n=>n.id===id?{...n,read:true}:n);
    savePayNotices(all); setNotices(all);
  };

  const exportCSV=()=>{
    const rows=[['Name','Username','Date','Day','Time In','Time Out','Break(min)','Hours','Notes']];
    filtered.forEach(e=>rows.push([e.displayName,e.username,e.date,
      new Date(e.date+'T12:00:00').toLocaleDateString('en-US',{weekday:'short'}),
      e.timeIn,e.timeOut,e.breakMins,((e.mins||0)/60).toFixed(2),e.notes||'']));
    const a=document.createElement('a');
    a.href=URL.createObjectURL(new Blob([rows.map(r=>r.map(v=>`"${v}"`).join(',')).join('\n')],{type:'text/csv'}));
    a.download=`Sterling_Hours_${today()}.csv`; a.click();
    toast('success','CSV exported');
  };

  const inp = {border:'1px solid #cdd3e4',borderRadius:4,padding:'7px 10px',
    fontSize:12,outline:'none',width:'100%',boxSizing:'border-box'};

  const tabBtn=(id,lbl)=>(
    <button onClick={()=>setTab(id)} style={{padding:'8px 18px',border:'none',
      background:'none',cursor:'pointer',fontSize:12,fontWeight:600,
      color:tab===id?'#1e3060':'#7788aa',
      borderBottom:tab===id?'2px solid #1e3060':'2px solid transparent',
      display:'flex',alignItems:'center',gap:5}}>
      {lbl}
    </button>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'}}>

      {/* Tab bar */}
      <div style={{background:'white',borderBottom:'2px solid #e0e4ec',
        padding:'0 12px',display:'flex',flexShrink:0,alignItems:'center'}}>
        {tabBtn('entry', editId?'✏️ Edit Entry':'⏱ Log Hours')}
        {tabBtn('mine', <>📋 My Hours {myWeek>0&&<span style={{background:'#1e3060',color:'white',borderRadius:8,padding:'1px 6px',fontSize:9}}>{fmtH(myWeek)} wk</span>}</>)}
        {unread>0 && tabBtn('notices', <><span>💰 Payment</span><span style={{background:'#b83232',color:'white',borderRadius:8,padding:'1px 6px',fontSize:9,marginLeft:4}}>{unread}</span></>)}
        {!unread && tabBtn('notices','💰 Payment')}
        {isAdmin && tabBtn('admin','👥 All Employees')}
      </div>

      {/* ── LOG HOURS ── */}
      {tab==='entry' && (
        <div className="content">
          <div style={{maxWidth:540}}>
            <div style={{background:'#1e3060',color:'white',borderRadius:8,
              padding:'12px 16px',marginBottom:14,display:'flex',alignItems:'center',gap:10}}>
              <div style={{width:36,height:36,borderRadius:'50%',background:'rgba(255,255,255,0.2)',
                fontSize:16,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center'}}>
                {(dispName||'?')[0]?.toUpperCase()}
              </div>
              <div>
                <div style={{fontWeight:700,fontSize:14}}>{dispName}'s Hours Log</div>
                <div style={{fontSize:11,color:'rgba(255,255,255,0.7)'}}>
                  {fmtH(myWeek)} this week · {fmtH(myMonth)} this month · {fmtH(myTotal)} total
                </div>
              </div>
            </div>
            <div className="panel">
              <div className="panel-header">{editId?'✏️ Edit Entry':'⏱ Log Hours'}</div>
              <div className="panel-body">
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label>Date</label>
                    <input type="date" value={form.date} onChange={e=>set('date',e.target.value)} style={inp}/>
                  </div>
                  <div className="form-group">
                    <label>Time In</label>
                    <input type="time" value={form.timeIn} onChange={e=>set('timeIn',e.target.value)} style={inp}/>
                  </div>
                  <div className="form-group">
                    <label>Time Out</label>
                    <input type="time" value={form.timeOut} onChange={e=>set('timeOut',e.target.value)} style={inp}/>
                  </div>
                  <div className="form-group">
                    <label>Break (minutes)</label>
                    <input type="number" min="0" max="120" value={form.breakMins} onChange={e=>set('breakMins',e.target.value)} style={inp}/>
                  </div>
                  <div className="form-group">
                    <label>Hours Worked</label>
                    <div style={{padding:'7px 10px',background:'#f0f6ff',borderRadius:4,
                      border:'1px solid #aac8ee',fontSize:18,fontWeight:700,
                      color:'#1e3060',textAlign:'center'}}>
                      {preview>0?fmtH(preview):'—'}
                    </div>
                  </div>
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label>Notes (optional)</label>
                    <input value={form.notes} onChange={e=>set('notes',e.target.value)}
                      placeholder="e.g. Sample analysis, instrument calibration..." style={inp}/>
                  </div>
                </div>
                <div style={{display:'flex',gap:8,marginTop:12}}>
                  <button className="btn btn-primary" style={{padding:'8px 24px',fontWeight:700}} onClick={handleSave}>
                    {editId?'💾 Update':'✅ Submit Hours'}
                  </button>
                  {editId && <button className="btn" onClick={()=>{setEditId(null);setForm({date:today(),timeIn:'',timeOut:'',breakMins:'30',notes:''});}}>Cancel</button>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MY HOURS ── */}
      {tab==='mine' && (
        <div className="content">
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:14,maxWidth:540}}>
            {[{l:'This Week',v:fmtH(myWeek),c:'#1a7dc4'},{l:'This Month',v:fmtH(myMonth),c:'#1a6b42'},{l:'All Time',v:fmtH(myTotal),c:'#1e3060'}].map(s=>(
              <div key={s.l} style={{background:'white',border:'1px solid #e0e4ec',borderRadius:8,
                padding:'12px 14px',textAlign:'center',borderTop:`3px solid ${s.c}`}}>
                <div style={{fontSize:22,fontWeight:700,color:s.c}}>{s.v}</div>
                <div style={{fontSize:10,color:'#888',textTransform:'uppercase',letterSpacing:'0.4px',marginTop:2}}>{s.l}</div>
              </div>
            ))}
          </div>
          <div className="panel">
            <div className="panel-header">
              📋 My Hours — {dispName}
              <span style={{marginLeft:'auto',fontSize:11,fontWeight:400,color:'#aac4e0'}}>
                {mine.length} entries
              </span>
            </div>
            <div style={{overflowX:'auto'}}>
              <table className="data-table">
                <thead><tr>
                  <th>Date</th><th>Day</th><th>Time In</th><th>Time Out</th>
                  <th>Break</th><th>Hours</th><th>Notes</th><th>Actions</th>
                </tr></thead>
                <tbody>
                  {mine.length===0
                    ? <tr><td colSpan={8} style={{textAlign:'center',padding:24,color:'#999'}}>No entries yet — log your first hours above</td></tr>
                    : mine.map(e=>(
                    <tr key={e.id}>
                      <td><strong>{e.date}</strong></td>
                      <td style={{color:'#888',fontSize:11}}>{new Date(e.date+'T12:00:00').toLocaleDateString('en-US',{weekday:'long'})}</td>
                      <td style={{textAlign:'center',fontFamily:'monospace'}}>{e.timeIn}</td>
                      <td style={{textAlign:'center',fontFamily:'monospace'}}>{e.timeOut}</td>
                      <td style={{textAlign:'center',color:'#888'}}>{e.breakMins}m</td>
                      <td style={{textAlign:'center'}}>
                        <span style={{fontWeight:700,color:'#1e3060',background:'#e8f0ff',padding:'2px 10px',borderRadius:8}}>{fmtH(e.mins)}</span>
                      </td>
                      <td style={{fontSize:11,color:'#666'}}>{e.notes||'—'}</td>
                      <td style={{display:'flex',gap:4}}>
                        <button className="btn btn-sm" onClick={()=>handleEdit(e)}>✏️</button>
                        <button className="btn btn-sm" style={{color:'#b83232'}} onClick={()=>setDelId(e.id)}>🗑</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── PAYMENT NOTICES ── */}
      {tab==='notices' && (
        <div className="content">
          <div style={{maxWidth:600}}>
            {myNotices.length===0
              ? <div style={{textAlign:'center',padding:40,color:'#999'}}>
                  <div style={{fontSize:32,marginBottom:12}}>💰</div>
                  <div style={{fontSize:14}}>No payment notices yet</div>
                </div>
              : myNotices.map(n=>(
              <div key={n.id} style={{background:n.read?'#fafafa':'white',
                border:`1px solid ${n.read?'#e0e4ec':'#1a7dc4'}`,borderRadius:8,
                padding:'14px 16px',marginBottom:10,
                borderLeft:`4px solid ${n.read?'#ccc':'#1a7dc4'}`}}>
                {!n.read && <span style={{background:'#1a7dc4',color:'white',fontSize:9,
                  fontWeight:700,borderRadius:4,padding:'1px 6px',marginBottom:6,display:'inline-block'}}>NEW</span>}
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
                  <span style={{fontSize:28}}>💰</span>
                  <div>
                    <div style={{fontWeight:700,fontSize:15,color:'#1e3060'}}>
                      Payment: <span style={{color:'#1a6b42'}}>${n.amount}</span>
                    </div>
                    <div style={{fontSize:12,color:'#666'}}>
                      Payment Date: <strong>{fmtDate(n.payDate)}</strong>
                    </div>
                  </div>
                  <div style={{marginLeft:'auto',fontSize:11,color:'#aaa',textAlign:'right'}}>
                    <div>From: {n.sentBy}</div>
                    <div>{new Date(n.sentAt).toLocaleDateString()}</div>
                  </div>
                </div>
                {n.note && <div style={{fontSize:12,color:'#555',background:'#f8f9ff',
                  borderRadius:4,padding:'8px 10px',marginBottom:8}}>{n.note}</div>}
                {!n.read && (
                  <button className="btn btn-sm" onClick={()=>markRead(n.id)}
                    style={{fontSize:11}}>✓ Mark as Read</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── ADMIN ── */}
      {tab==='admin' && isAdmin && (
        <div className="content">
          {/* Employee summary cards */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:12,marginBottom:16}}>
            {byEmployee.length===0
              ? <div style={{gridColumn:'1/-1',textAlign:'center',padding:24,color:'#999'}}>No hours logged yet</div>
              : byEmployee.map(u=>{
                const unpaid = notices.filter(n=>n.username===u.username).length;
                return (
                <div key={u.username} style={{background:'white',border:'1px solid #e0e4ec',
                  borderRadius:8,padding:'14px 16px',borderTop:'3px solid #1e3060'}}>
                  <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
                    <div style={{width:40,height:40,borderRadius:'50%',background:'#1e3060',
                      color:'white',fontSize:16,fontWeight:700,display:'flex',
                      alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      {(u.displayName||u.username)[0]?.toUpperCase()}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:13,color:'#1e3060'}}>{u.displayName}</div>
                      <div style={{fontSize:10,color:'#aaa'}}>{u.username}</div>
                    </div>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:10}}>
                    <div style={{background:'#f0f6ff',borderRadius:4,padding:'6px 8px',textAlign:'center'}}>
                      <div style={{fontSize:16,fontWeight:700,color:'#1e3060'}}>{fmtH(u.mins)}</div>
                      <div style={{fontSize:9,color:'#888',textTransform:'uppercase'}}>Total Hours</div>
                    </div>
                    <div style={{background:'#f0f6ff',borderRadius:4,padding:'6px 8px',textAlign:'center'}}>
                      <div style={{fontSize:16,fontWeight:700,color:'#1e3060'}}>{u.days}</div>
                      <div style={{fontSize:9,color:'#888',textTransform:'uppercase'}}>Days Worked</div>
                    </div>
                  </div>
                  <div style={{fontSize:10,color:'#888',marginBottom:10}}>
                    Last entry: <strong>{fmtDate(u.lastDate)}</strong>
                  </div>
                  <button className="btn btn-primary" style={{width:'100%',fontSize:11,fontWeight:700}}
                    onClick={()=>{setPayModal(u);setPayForm({amount:'',payDate:'',note:''});}}>
                    💰 Send Payment Notice
                  </button>
                </div>
              );})}
          </div>

          {/* Pay notices sent history */}
          {notices.length>0 && (
            <div className="panel" style={{marginBottom:14}}>
              <div className="panel-header">💰 Payment Notices Sent</div>
              <div style={{overflowX:'auto'}}>
                <table className="data-table">
                  <thead><tr>
                    <th>Employee</th><th>Amount</th><th>Pay Date</th>
                    <th>Note</th><th>Sent</th><th>Status</th>
                  </tr></thead>
                  <tbody>
                    {[...notices].sort((a,b)=>b.sentAt.localeCompare(a.sentAt)).map(n=>(
                      <tr key={n.id}>
                        <td><strong>{n.displayName}</strong><div style={{fontSize:10,color:'#aaa'}}>{n.username}</div></td>
                        <td><strong style={{color:'#1a6b42',fontSize:13}}>${n.amount}</strong></td>
                        <td>{fmtDate(n.payDate)}</td>
                        <td style={{fontSize:11,color:'#666'}}>{n.note||'—'}</td>
                        <td style={{fontSize:11,color:'#888'}}>{new Date(n.sentAt).toLocaleDateString()}</td>
                        <td>
                          <span style={{fontSize:10,padding:'2px 8px',borderRadius:8,fontWeight:700,
                            background:n.read?'#d4edda':'#fff3cd',
                            color:n.read?'#155724':'#856404'}}>
                            {n.read?'✓ Read':'⏳ Unread'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Filters + all entries */}
          <div className="toolbar" style={{marginBottom:10,borderRadius:6,border:'1px solid #e0e4ec'}}>
            <span className="tb-label">Filter:</span>
            <input placeholder="Employee..." value={fUser} onChange={e=>setFUser(e.target.value)}
              style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 8px',fontSize:11,outline:'none',width:140}}/>
            <input type="date" value={fFrom} onChange={e=>setFFrom(e.target.value)}
              style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 8px',fontSize:11,outline:'none'}}/>
            <span style={{color:'#aaa',fontSize:11}}>to</span>
            <input type="date" value={fTo} onChange={e=>setFTo(e.target.value)}
              style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 8px',fontSize:11,outline:'none'}}/>
            {(fUser||fFrom||fTo)&&<button className="btn btn-sm" onClick={()=>{setFUser('');setFFrom('');setFTo('');}}>✕</button>}
            <div className="tb-sep"/>
            <span className="tb-label">{filtered.length} entries · {fmtH(filtered.reduce((a,e)=>a+(e.mins||0),0))}</span>
            <div className="tb-sep"/>
            <button className="btn" onClick={exportCSV}>📥 CSV</button>
          </div>
          <div className="panel">
            <div className="panel-header">📋 All Time Entries</div>
            <div style={{overflowX:'auto'}}>
              <table className="data-table">
                <thead><tr>
                  <th>Employee</th><th>Date</th><th>Day</th>
                  <th>In</th><th>Out</th><th>Break</th><th>Hours</th><th>Notes</th><th>Delete</th>
                </tr></thead>
                <tbody>
                  {filtered.length===0
                    ? <tr><td colSpan={9} style={{textAlign:'center',padding:20,color:'#999'}}>No entries</td></tr>
                    : filtered.map(e=>(
                    <tr key={e.id}>
                      <td><div style={{fontWeight:600}}>{e.displayName}</div><div style={{fontSize:10,color:'#aaa'}}>{e.username}</div></td>
                      <td><strong>{e.date}</strong></td>
                      <td style={{color:'#888',fontSize:11}}>{new Date(e.date+'T12:00:00').toLocaleDateString('en-US',{weekday:'short'})}</td>
                      <td style={{fontFamily:'monospace',fontSize:11}}>{e.timeIn}</td>
                      <td style={{fontFamily:'monospace',fontSize:11}}>{e.timeOut}</td>
                      <td style={{color:'#888',fontSize:11}}>{e.breakMins}m</td>
                      <td><span style={{fontWeight:700,color:'#1e3060',background:'#e8f0ff',padding:'2px 8px',borderRadius:8,fontSize:12}}>{fmtH(e.mins)}</span></td>
                      <td style={{fontSize:11,color:'#666'}}>{e.notes||'—'}</td>
                      <td><button className="btn btn-sm" style={{color:'#b83232'}} onClick={()=>setDelId(e.id)}>🗑</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Payment notice modal */}
      {payModal && (
        <div className="modal-overlay" onClick={()=>setPayModal(null)}>
          <div className="modal" style={{width:460}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header" style={{background:'#1a6b42'}}>
              <h2>💰 Send Payment Notice</h2>
              <button className="modal-close" onClick={()=>setPayModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14,
                padding:'10px 12px',background:'#f0f6ff',borderRadius:6}}>
                <div style={{width:36,height:36,borderRadius:'50%',background:'#1e3060',
                  color:'white',fontSize:15,fontWeight:700,display:'flex',
                  alignItems:'center',justifyContent:'center'}}>
                  {(payModal.displayName||'?')[0]?.toUpperCase()}
                </div>
                <div>
                  <div style={{fontWeight:700}}>{payModal.displayName}</div>
                  <div style={{fontSize:11,color:'#888'}}>
                    Total hours: <strong>{fmtH(byEmployee.find(u=>u.username===payModal.username)?.mins||0)}</strong>
                  </div>
                </div>
              </div>
              <div className="form-group" style={{marginBottom:12}}>
                <label>Payment Amount ($) *</label>
                <input type="number" min="0" step="0.01" value={payForm.amount}
                  onChange={e=>setPayForm(p=>({...p,amount:e.target.value}))}
                  placeholder="e.g. 450.00"
                  style={{...inp,fontSize:16,fontWeight:700}}/>
              </div>
              <div className="form-group" style={{marginBottom:12}}>
                <label>Payment Date *</label>
                <input type="date" value={payForm.payDate}
                  onChange={e=>setPayForm(p=>({...p,payDate:e.target.value}))}
                  style={inp}/>
              </div>
              <div className="form-group">
                <label>Message to Employee (optional)</label>
                <textarea value={payForm.note}
                  onChange={e=>setPayForm(p=>({...p,note:e.target.value}))}
                  placeholder="e.g. Payment for June 1-15 hours. Please confirm receipt."
                  rows={3}
                  style={{...inp,resize:'vertical',fontFamily:'inherit'}}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-success" style={{fontWeight:700}}
                onClick={sendPayNotice}>
                💰 Send Payment Notice
              </button>
              <button className="btn" onClick={()=>setPayModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {delId && (
        <div className="modal-overlay" onClick={()=>setDelId(null)}>
          <div className="modal" style={{width:360}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header" style={{background:'#b83232'}}>
              <h2>🗑 Delete Entry</h2>
              <button className="modal-close" onClick={()=>setDelId(null)}>✕</button>
            </div>
            <div className="modal-body"><p>Delete this hours entry? Cannot be undone.</p></div>
            <div className="modal-footer">
              <button className="btn btn-danger" onClick={()=>handleDelete(delId)}>🗑 Delete</button>
              <button className="btn" onClick={()=>setDelId(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
